/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { NewsMediaCardFragment } from "@cosmos/hygraph-schema";
import {
	ButtonList,
	CosmosAsset,
	CosmosCard,
	CosmosDesc,
	CosmosText,
	CosmosTextVariantEnum,
	CosmosTitle,
	hasContent,
} from "@components/elements";
import type { OptimizedCosmosAssetFragment } from "@components/elements";
import type { AnalyticsProps } from "@components/types";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";

const newsMediaCardContentStyles = css({
	paddingBlockStart: "eds.dimension.24",
	flexGrow: "1",
	justifyContent: "space-between",
});

const newsMediaCardTitleStyles = css({
	lineClamp: "2",
	textOverflow: "ellipsis",
	overflow: "hidden",
	alignSelf: "start",
	textAlign: "start",
	textWrap: "unset",
});

const newsMediaCardDescStyles = css({
	lineClamp: "5",
	textOverflow: "ellipsis",
	overflow: "hidden",
});

const newsMediaThumbnailStyles = css({ height: "auto", minHeight: "fit-content", width: "100%" });
const newsMediaAuthorStyles = css({ fontWeight: "medium" });

export function NewsMediaCard({
	asset,
	href,
	title,
	desc,
	author,
	analytics,
	modButtons,
}: NewsMediaCardFragment & AnalyticsProps): React.JSX.Element {
	const sizedAsset: OptimizedCosmosAssetFragment = {
		__typename: "Asset",
		...(asset || {}),
		id: asset?.id || "",
		url: asset?.url || "",
		width: 392,
		height: 221,
	};

	const hasTitle = hasContent(title);
	const hasDesc = hasContent(desc);
	const hasAuthor = hasContent(author);
	const hasButtons = modButtons?.length > 0;

	return (
		<CosmosCard padding={16} variant="material" className="news-media-card">
			<CosmosCard.Thumbnail aspectRatio="16/9" className={newsMediaThumbnailStyles}>
				<CosmosAsset asset={sizedAsset} />
			</CosmosCard.Thumbnail>
			<CosmosCard.Content className={newsMediaCardContentStyles}>
				<VStack alignItems="stretch" gap="eds.dimension.16" justifyContent="space-between" h="100%">
					{hasTitle || hasDesc ? (
						<VStack alignItems="start" gap="eds.dimension.8">
							<CosmosCard.Link href={href || ""} analytics={analytics}>
								<CosmosTitle
									title={{ html: title?.html, text: title?.text, variant: CosmosTextVariantEnum.headingSM }}
									className={newsMediaCardTitleStyles}
								/>
							</CosmosCard.Link>
							{hasDesc ? (
								<CosmosDesc
									description={{ variant: "paragraphMD", color: "eds.text.secondary", html: desc }}
									className={newsMediaCardDescStyles}
								/>
							) : null}
						</VStack>
					) : null}
					{hasAuthor ? (
						<CosmosText variant="uiSM" color="eds.text.primary" text={author || ""} className={newsMediaAuthorStyles} />
					) : null}
					{hasButtons ? <ButtonList buttons={modButtons} /> : null}
				</VStack>
			</CosmosCard.Content>
		</CosmosCard>
	);
}
